import React from "react";
import {
  Alert, Box, IconButton, Menu, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { LegacyTokens, B13, B13Ink, tint12 } from "../../../../theme/b13Tokens";

// Batch 7C Step 2 — stacked maintenance-interval schedule per phase.
//
// Row anatomy per §7C.1: [Frequency] for [N] [unit]
//   Frequency comes from the phase-interval enum on the row.
//   N and unit come from forCount + forUnit; NULL for both = "forever"
//   (typically the last row of the fade).
//
// Adding "daily" later — either as a seventh menu option or as an
// implicit first row — is a change in ONE PLACE: the FREQUENCY_MENU
// constant below. Everything else (row rendering, edit affordance,
// backend save shape) already handles 'daily' because the DB enum
// includes it (Batch 7C Step 2 migration). See report §"Extension
// point" in the batch commit.

export type IntervalRow = {
  id: number;
  frequency: "daily" | "weekly" | "bi-weekly" | "monthly" | "quarterly" | "semi-annually" | "annually";
  forCount: number | null;
  forUnit: "day" | "week" | "month" | null;
  orderNumber: number;
};

// The client's ADD MAINTENANCE INTERVAL menu — six values verbatim
// from BATCH_7C_SPEC_SECTION.md §7C.1, in the order specified.
// `daily` is NOT here pending the client answer; it IS a valid
// stored value if the answer comes back as "implicit first row" or
// "add to menu" — adding it to this array is the one-line change.
const FREQUENCY_MENU: Array<IntervalRow["frequency"]> = [
  // T63 Part O — 'daily' promoted to first-class cadence on the
  // interval frequency enum (backend migration 2026_08_30_100000).
  // Standard workflow factory seeds Daily / 12 days out-of-box.
  "daily",
  "weekly",
  "bi-weekly",
  "monthly",
  "quarterly",
  "semi-annually",
  "annually",
];

const FREQUENCY_LABEL: Record<IntervalRow["frequency"], string> = {
  daily:          "Daily",
  weekly:         "Weekly",
  "bi-weekly":    "Bi-weekly",
  monthly:        "Monthly",
  quarterly:      "Quarterly",
  "semi-annually":"Semi-annually",
  annually:       "Annually",
};

const UNIT_LABEL: Record<NonNullable<IntervalRow["forUnit"]>, string> = {
  day:   "days",
  week:  "weeks",
  month: "months",
};

export type MaintenanceIntervalRowsProps = {
  rows: IntervalRow[];
  onAdd: (frequency: IntervalRow["frequency"]) => void;
  onUpdate: (id: number, patch: Partial<IntervalRow>) => void;
  onDelete: (id: number) => void;
  readOnly?: boolean;
};

// Verbatim from BATCH_7C_SPEC_SECTION.md §7C.1 including the
// apostrophe error in "it's" — matching the client's copy is the
// priority over grammar per the spec instruction.
const INFO_BANNER = "If a target does not meet the criteria set below it will appear daily until criteria are met, regardless of it's current maintenance interval. You can read more about maintenance schedules in the Knowledge Base.";

export default function MaintenanceIntervalRows({
  rows, onAdd, onUpdate, onDelete, readOnly,
}: MaintenanceIntervalRowsProps) {
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const sorted = [...rows].sort((a, b) => a.orderNumber - b.orderNumber);

  return (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: B13Ink.ink3, mb: 1 }}>
        Interval
      </Typography>

      {sorted.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: B13Ink.ink3, fontStyle: "italic", mb: 1.5 }}>
          No maintenance intervals configured yet.
        </Typography>
      ) : (
        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
          {sorted.map((row) => (
            <IntervalRowView
              key={row.id}
              row={row}
              onUpdate={(patch) => onUpdate(row.id, patch)}
              onDelete={() => onDelete(row.id)}
              readOnly={readOnly}
            />
          ))}
        </Stack>
      )}

      {!readOnly && (
        <>
          <Box
            role="button"
            tabIndex={0}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setMenuAnchor(e.currentTarget as HTMLElement); } }}
            sx={{
              display: "inline-flex", alignItems: "center", gap: 0.25,
              cursor: "pointer", userSelect: "none",
              color: B13.primary,
              fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
              py: 0.5,
              "&:hover": { textDecoration: "underline" },
              "&:focus-visible": { outline: `2px solid ${B13.primary}`, outlineOffset: 2 },
            }}
          >
            + Add maintenance interval <ArrowDropDownIcon sx={{ fontSize: 18 }} />
          </Box>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            {FREQUENCY_MENU.map((f) => (
              <MenuItem key={f} onClick={() => { onAdd(f); setMenuAnchor(null); }}>
                {FREQUENCY_LABEL[f]}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      <Alert
        severity="info"
        icon={false}
        sx={{
          mt: 1.5,
          fontSize: 12,
          backgroundColor: tint12(B13.primary),
          color: B13Ink.ink2,
          border: `1px solid ${B13.line}`,
          borderRadius: `${LegacyTokens.radius.chip}px`,
          "& .MuiAlert-message": { p: 0 },
        }}
      >
        {INFO_BANNER}
      </Alert>
    </Box>
  );
}

// One row in the stack. "[Frequency] for [N] [unit]" — with the
// duration pair editable in-line and a delete button on the right.
// A row whose forCount/forUnit are both NULL reads as "forever".
const IntervalRowView: React.FC<{
  row: IntervalRow;
  onUpdate: (patch: Partial<IntervalRow>) => void;
  onDelete: () => void;
  readOnly?: boolean;
}> = ({ row, onUpdate, onDelete, readOnly }) => {
  const isForever = row.forCount === null || row.forUnit === null;
  return (
    <Stack
      direction="row" alignItems="center" spacing={1}
      sx={{
        px: 1.25, py: 0.75,
        borderBottom: `1px solid ${B13.line}`,
        backgroundColor: "transparent",
      }}
    >
      <DragIndicatorIcon sx={{ fontSize: 16, color: B13Ink.ink3, flexShrink: 0 }} aria-hidden />
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: B13.ink, minWidth: 96 }}>
        {FREQUENCY_LABEL[row.frequency]}
      </Typography>
      <Typography sx={{ fontSize: 13, color: B13Ink.ink3 }}>for</Typography>
      {isForever ? (
        <Typography sx={{ fontSize: 13, color: B13Ink.ink3, fontStyle: "italic" }}>
          forever
        </Typography>
      ) : (
        <>
          <TextField
            size="small"
            type="number"
            value={row.forCount ?? ""}
            disabled={readOnly}
            onChange={(e) => {
              const n = e.target.value === "" ? null : Math.max(1, Number(e.target.value));
              onUpdate({ forCount: n });
            }}
            inputProps={{ min: 1, style: { width: 48, textAlign: "right", fontVariantNumeric: "tabular-nums" } }}
            sx={{ "& .MuiOutlinedInput-root": { height: 32 } }}
          />
          <TextField
            size="small"
            select
            SelectProps={{ native: true }}
            value={row.forUnit ?? "day"}
            disabled={readOnly}
            onChange={(e) => onUpdate({ forUnit: e.target.value as IntervalRow["forUnit"] })}
            sx={{ "& .MuiOutlinedInput-root": { height: 32, minWidth: 80 } }}
          >
            <option value="day">{UNIT_LABEL.day}</option>
            <option value="week">{UNIT_LABEL.week}</option>
            <option value="month">{UNIT_LABEL.month}</option>
          </TextField>
        </>
      )}
      <Box sx={{ flexGrow: 1 }} />
      {!readOnly && (
        <IconButton
          aria-label="Remove interval"
          onClick={onDelete}
          sx={{
            width: 28, height: 28,
            color: B13Ink.ink3,
            "&:hover": { color: B13.ink, backgroundColor: B13.canvas },
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Stack>
  );
};
